import ajax from "@ep/tools/ajax";
import accountsClient from "@ep/auth-client/client";
import session from "@ep/tools/session";
import Format from "@ep/tools/format";
import msgbox from "@ep/tools/msgbox";
import toast from "@ep/tools/toast";
import { getDataFields } from "@ep/components/create/autoform";
import { MaskEmail } from "@ep/tools/tools";
import { isDevelopmentAppEnv } from "@ep/tools/env";
import { getDeviceId } from "@ep/tools/deviceId";

let sharedCatalogCache = null;
let sharedCatalogPromise = null;
const TYPE_KEYS = ['personal', 'autonomous', 'mei', 'enterprise'];

/**
 * Controla seleção, precificação e registro de módulos na área pública.
 */
class Modules {
  renderVersion = 0;
  initialRenderBatchSize = 2;
  renderBatchSize = 2;
  selectedModules = new Set();
  selectedCards = [];
  moduleMeta = new Map();
  moduleNameById = new Map();
  manuallySelectedModules = new Set();
  modulePrices = new Map();
  moduleServers = new Map();
  syncingSelection = false;
  suiteNames = [];
  suiteModules = new Map();
  data = null;
  currentType = 'enterprise';
  dependencyLocks = new Map();
  dependencyPromptOpen = new Set();
  format = new Format();
  clickLocks = new Map();
  registering = false;

  #isDevelopment() {
    return isDevelopmentAppEnv();
  }

  #getModuleImage(module) {
    const isDark = document.body.classList.contains('dark');
    const light = String(module?.image_link_light || '').trim();
    const dark = String(module?.image_link_dark || '').trim();
    return isDark ? (dark || light || null) : (light || dark || null);
  }

  async #yieldToBrowser() {
    await new Promise((resolve) => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 120 });
        return;
      }

      setTimeout(resolve, 16);
    });
  }

  #prepareSuitesData(rawSuites = [], moduleServers = {}) {
    const language = session.get('language') || 'pt';
    const source = Array.isArray(rawSuites) ? rawSuites : [];

    return source
      .map((item) => {
        const moduleKey = this.#getModuleKey(item);
        const featureNames = this.#coerceArray(dic.modules?.[moduleKey]?.features)
          .filter((feature) => typeof feature === 'string' && feature.trim())
          .map((feature) => feature.trim());

        return {
          ...item,
          groupName: this.#getSuiteName(item?.key, 'catalog'),
          display: this.#getModuleName(moduleKey, 'catalog'),
          description: dic.modules?.[moduleKey]?.description || '',
          server: moduleServers[moduleKey] || item?.key,
          featureNames
        };
      })
      .sort((a, b) => {
        const aOrder = Number(a?.order ?? Number.POSITIVE_INFINITY);
        const bOrder = Number(b?.order ?? Number.POSITIVE_INFINITY);
        if (Number.isFinite(aOrder) || Number.isFinite(bOrder)) {
          const byOrder = aOrder - bOrder;
          if (byOrder !== 0) return byOrder;
        }

        const byGroup = String(a?.groupName || '').localeCompare(String(b?.groupName || ''), language, { sensitivity: "accent" });
        if (byGroup !== 0) return byGroup;

        return String(a?.display || '').localeCompare(String(b?.display || ''), language, { sensitivity: "accent" });
      });
  }

  #getModuleKey(item) {
    return String(item?.module || item?.domain || '').trim();
  }

  #humanizeModuleKey(moduleKey) {
    return String(moduleKey || '')
      .trim()
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  #getModuleName(moduleKey, context = 'catalog') {
    const entry = dic.modules?.[moduleKey];
    if (entry && typeof entry === 'object') {
      return entry[context] || entry.catalog || entry.menu || entry.display || this.#humanizeModuleKey(moduleKey);
    }
    return entry || this.#humanizeModuleKey(moduleKey);
  }

  #getSuiteName(suiteKey, context = 'catalog') {
    const entry = dic.suites?.[suiteKey];
    if (entry && typeof entry === 'object') {
      return entry[context] || entry.catalog || entry.menu || suiteKey;
    }
    return entry || suiteKey;
  }

  #toBool(value) {
    return value === true || value === 't' || value === 'true' || value === 1 || value === '1';
  }

  #coerceArray(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [raw];
      }
    }
    return [];
  }

  #getTypeCatalogKey(type = this.currentType) {
    const normalizedType = String(type || '').trim().toLowerCase();
    switch (normalizedType) {
      case 'personal':
        return 'suitesPersonal';
      case 'autonomous':
        return 'suitesAutonomous';
      case 'mei':
        return 'suitesMei';
      case 'enterprise':
        return 'suitesEnterprise';
      default:
        return 'suitesPersonal';
    }
  }

  #getTypeUsersConfig(type = this.currentType) {
    const normalizedType = String(type || '').trim().toLowerCase();
    if (normalizedType === 'enterprise') {
      return { value: 5, min: 5, disabled: false };
    }
    if (normalizedType === 'mei') {
      return { value: 3, min: 3, disabled: true };
    }
    return { value: 1, min: 1, disabled: true };
  }

  #normalizeUsersQtyForType(rawValue, type = this.currentType) {
    const config = this.#getTypeUsersConfig(type);
    let value = Number(rawValue);
    if (!Number.isFinite(value) || value <= 0) {
      value = config.value;
    }
    value = Math.floor(value);
    if (config.disabled) {
      return config.value;
    }
    return Math.max(config.min, value);
  }

  #scrollToModuleCard(moduleId) {
    const key = String(moduleId || '').trim();
    if (!key) return;
    const target = document.querySelector(`a[name="${key}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ===== Validacao e selecao ===== */
  /**
   * Sincroniza os checkboxes de módulos com o estado selecionado atual.
   * @private
   */
  #applySelections() {
    this.syncingSelection = true;
    const inputs = new Set([
      ...document.querySelectorAll('input.module-selector'),
      ...document.querySelectorAll('input[data-module-id]')
    ]);
    const modulesToSync = new Set();
    inputs.forEach(input => {
      const moduleId = this.#getModuleKeyFromInput(input);
      if (!moduleId) return;
      input.checked = this.selectedModules.has(moduleId);
      input.disabled = this.dependencyLocks.has(moduleId);
      modulesToSync.add(moduleId);
    });
    // Garantir que duplicados em outras suites reflitam o estado atual
    modulesToSync.forEach(id => this.#syncModuleState(id));
    // Atualiza o estado dos toggles de suite
    this.suiteModules.forEach((_, groupName) => this.#updateSuiteToggle(groupName));
    this.syncingSelection = false;
  }

  /**
   * Aplica seleção de módulo incluindo suas dependências.
   * @private
   * @param {*} module
   * @param {*} dependencies
   * @param {*} sourceInput
   */
  #applySelectionWithDependencies(module, dependencies, sourceInput) {
    this.syncingSelection = true;
    this.#toggleModuleSelection(this.#getModuleKey(module), true, sourceInput, true, module);
    for (const dep of dependencies) {
      const depModuleId = this.#getDepModuleId(dep);
      this.#toggleModuleSelection(depModuleId, true, sourceInput, false);
    }
    this.#updateDependencyLocks(module, true);
    this.syncingSelection = false;
    this.#applySelections();
    // Garantir que os switches reflitam o estado após o fechamento do modal
    requestAnimationFrame(() => {
      this.#applySelections();
      // Sincroniza diretamente os inputs dos módulos afetados (inclusive duplicados em outras suites)
      const affected = [this.#getModuleKey(module), ...dependencies.map(dep => this.#getDepModuleId(dep)).filter(Boolean)];
      affected.forEach(id => this.#syncModuleState(id));
    });
    this.#updateCart();
  }

  /**
   * Monta a mensagem de confirmação para inclusão de dependências.
   * @private
   * @param {*} depsToAdd
   */
  #buildDependencyPromptMessage(depsToAdd) {
    let message = `
      <div>
      <div>${dic.register.askDependencies2}</div>
      <ul>
    `;

    for (const dep of depsToAdd) {
      const depModuleId = this.#getDepModuleId(dep);
      const display = this.#getModuleName(depModuleId, 'catalog');
      const depPrice = Number(dep?.price ?? dep?.module_price ?? dep?.price_brl ?? dep?.price_usd);
      const showPrice = Number.isFinite(depPrice);
      message += `<li>
        ${display}
        ${showPrice ? `${dic.register.addPrice}${this.format.number(depPrice, { decimalPlaces: 2 })})` : ''}
      </li>`;
    }

    message += `</ul><div>${dic.register.askDependencies3}</div></div>`;
    return message;
  }

  /**
   * Monta o controle de seleção de um módulo no card.
   * @private
   * @param {*} module
   */
  #buildModuleCheckbox(module) {
    const server = module.server || module.key;
    const isMandatory = this.#toBool(module?.is_mandatory ?? module?.mandatory);

    if (isMandatory) {
      this.#toggleModuleSelection(module.module || module.domain, true, null, false, module);
      return { element: 'div', class: 'module-included', text: `✓ ${dic.register.included}` };
    }

    return {
      element: 'checkbox',
      class: 'module-selector',
      fbClass: 'module-select',
      id: `module-select-${module.module || module.domain}-${module.key}`,
      text: this.selectedModules.has(module.module || module.domain)
        ? (dic.register.selectedModule || dic.register.selectModule)
        : dic.register.selectModule,
      value: module.module || module.domain,
      data: { id: module.domain_id ?? module.module_id, key: module.module || module.domain, server },
      appearance: 'switch',
      checked: this.selectedModules.has(module.module || module.domain),
      click: (e) => this.#onModuleToggle(module, e.currentTarget),
    };
  }

  /**
   * Calcula os totais do carrinho com base em módulos e quantidade de usuários.
   * @private
   * @param {*} usersQtyValue
   */
  #calculateCart(usersQtyValue) {
    const moduleCount = this.selectedModules.size;
    let modulesTotal = 0;

    for (const key of this.selectedModules) {
      const priceInfo = this.modulePrices.get(key);
      const priceBase = typeof priceInfo === 'object' ? Number(priceInfo.base ?? 0) : Number(priceInfo ?? 0);
      modulesTotal += priceBase;
    }

    const userRange = this.#getUserPriceRange(usersQtyValue);
    const usersTotal = Number(userRange?.price ?? 0) * usersQtyValue;
    const total = modulesTotal + usersTotal;
    const format = (value) => this.format.number(value, { decimalPlaces: 2 });

    return {
      moduleCount,
      modulesTotal,
      usersTotal,
      total,
      formattedModules: format(modulesTotal),
      formattedUsers: format(usersTotal),
      formattedTotal: format(total),
    };
  }

  /**
   * Libera lock temporário de interação de um módulo.
   * @private
   * @param {*} moduleId
   */
  #clearModuleInteraction(moduleId) {
    if (!moduleId) return;
    const timer = this.clickLocks.get(moduleId);
    if (timer) clearTimeout(timer);
    this.clickLocks.delete(moduleId);
  }

  /**
   * Fecha o prompt de dependências aberto para um módulo.
   * @private
   * @param {*} moduleId
   */
  #closeDependencyPrompt(moduleId) {
    const selector = moduleId ? `.module-box.module-box-${moduleId}` : '.module-box';
    document.querySelectorAll(selector).forEach(box => {
      try {
        if (typeof box.dialog === 'function') box.dialog('close');
      } catch (err) {
        // ignore close errors and remove element
      }
      box.remove();
    });
    if (moduleId) {
      this.dependencyPromptOpen.delete(moduleId);
    }
    else {
      this.dependencyPromptOpen.clear();
    }
  }

  /**
   * Valida formato básico de e-mail para o formulário.
   * @private
   * @param {*} value
   */
  #isValidEmail(value) {
    const email = String(value || '').trim();
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return emailRe.test(email);
  }

  /**
   * Atualiza estado visual de validação de um campo.
   * @private
   * @param {*} input
   * @param {*} isValid
   */
  #setFieldValidationState(input, isValid) {
    if (!input) return;
    const fieldbox = input.closest('.fieldbox');
    input.dataset.touched = 'true';
    input.data('valid', isValid === true);

    if (fieldbox) {
      fieldbox.classList.toggle('valid', isValid === true);
      fieldbox.classList.toggle('invalid', isValid === false);
      if (isValid === null) {
        fieldbox.classList.remove('valid');
        fieldbox.classList.remove('invalid');
      }
    }
  }
  /* ===== Renderizacao ===== */
  /**
   * Cria o layout do carrinho lateral e formulário de contratação.
   * @private
   */
  async #createAsideCart() {
    return {
      element: 'aside', parent: 'footer', append: 'before', id: 'module-aside-cart', class: 'module-aside-cart', isGrid: true, content: [
        {
          element: 'form', id: 'cart-form', isGrid: true, content: [
            {
              element: 'div', id: 'view-cart', isGrid: true, content: [
                {
                  element: 'group', type: 'fieldset', style: { marginBottom: '1rem' }, groups: [
                    {
                      legend: dic.register.accessData, isGrid: true, content: [
                        { element: 'text', id: 'user_name', name: 'user_name', label: dic.register.userName, placeholder: dic.register.nickname, required: true, row: 0, col: 0, full: true },
                        { element: 'email', id: 'email', name: 'email', required: true, row: 1, col: 0, full: true }
                      ]
                    },
                  ]
                },
                {
                  element: 'combo', label: dic.register.typeRegister, name: 'type-register',
                  value: 'enterprise', cols: 2, fbStyle: { margin: 'auto', width: '8rem' },
                  style: { marginBottom: '1rem', width:'8rem!important' }, sort: false, full:true,
                  options:
                    [
                      { text: dic.register.personal, value: 'personal' },
                      { text: dic.register.autonomous, value: 'autonomous' },
                      { text: dic.register.mei, value: 'mei' },
                      { text: dic.register.enterprise, value: 'enterprise' },
                    ], click: async (e) => {
                      const value = e.currentTarget?.dataset?.value;
                      await this.#handleTypeChange(value);
                    }
                },
                {
                  element: 'spinner', id: 'users-qty', mode: 'moreless', value: 5, step: 1, min: this.#getTypeUsersConfig().min, max: 10000000,
                  deferMinValidation: true, fbStyle: { margin: 'auto', width: 'fit-content' },
                  disabled: this.#getTypeUsersConfig().disabled,
                  label: dic.register.usersNum, style: { width: '4rem' },
                  change: () => this.#updateCart(), input: () => this.#updateCart(), blur: () => this.#updateCart()
                },
              ]
            },
            {
              element: 'div', id: 'module-cart-value', isGrid: true, content: [
                { element: 'hidden', id: 'cart_modules_qty', name: 'cart_modules_qty', value: 0 },
                { element: 'hidden', id: 'cart_modules_value', name: 'cart_modules_value', value: 0 },
                { element: 'hidden', id: 'cart_users_qty', name: 'cart_users_qty', value: 0 },
                { element: 'hidden', id: 'cart_users_value', name: 'cart_users_value', value: 0 },
                { element: 'hidden', id: 'cart_monthly_value', name: 'cart_monthly_value', value: 0 },
                // {
                //   element: 'div', isGrid: false, content: [
                //     { element: 'span', text: dic.register.moduleLabel1 },
                //     { element: 'span', id: 'text-modules-qty', text: '5' },
                //     { element: 'span', text: dic.register.moduleLabel2 },
                //     { element: 'span', id: 'text-modules-value', text: this.format.number(0, { decimalPlaces: 2 }) },
                //   ]
                // },
                // {
                //   element: 'div', isGrid: false, content: [
                //     { element: 'span', text: dic.register.usersLabel1 },
                //     { element: 'span', id: 'text-users-qty', text: '5' },
                //     { element: 'span', text: dic.register.usersLabel2 },
                //     { element: 'span', id: 'text-users-value', text: this.format.number(0, { decimalPlaces: 2 }) },
                //   ]
                // },
                { element: 'div', class: 'view-cart-text', text: dic.register.monthlyLabel1 },
                {
                  element: 'div', class: 'view-cart-total', isGrid: true, content: [
                    { element: 'span', text: dic.register.monthlyLabel2 },
                    { element: 'span', id: 'text-monthly-value', text: this.format.number(0, { decimalPlaces: 2 }) },
                    { element: 'span', text: dic.register.monthlyLabel3 },
                  ]
                },
              ]
            },
            {
              element: 'button', type: 'button', data: { button: 'enter' }, color: 'green', text: dic.register.activate, click: async (e) => {
                const dataFields = await getDataFields(e, { returnMode: 'object' });
                dataFields.cart_users_qty = Number(dataFields.cart_users_qty || this.#getUsersQtyValue() || this.#getTypeUsersConfig().value);
                dataFields.cart_users_value = Number(dataFields.cart_users_value || 0);
                dataFields.cart_monthly_value = Number(dataFields.cart_monthly_value || 0);
                dataFields.cards = this.selectedCards;
                const modulesQty = parseInt(dataFields.cart_modules_qty);
                let warning = '';
                const emailInput = document.getElementById('email');
                const trimmedEmail = String(dataFields.email || emailInput?.value || '').trim();
                const emailValid = this.#isValidEmail(trimmedEmail);
                dataFields.email = trimmedEmail;
                this.#setFieldValidationState(emailInput, emailValid);
                dataFields.password = '';

                if (isNaN(modulesQty) || modulesQty == 0) {
                  warning += `  - ${dic.register.mandatoryModule}<br>`;
                }

                if (!dataFields?.user_name || !dataFields?.email) {
                  warning += `  - ${dic.register.voidFields}<br>`;
                }

                if (warning.length === 0 && !emailValid) {
                  warning += `  - ${dic.register.invalidEmail}<br>`;
                }

                if (warning.length > 0) {
                  const message = `
                    <p>${dic.register.errors.RECORD}</p>
                    <br>
                    <blockquote>${warning}</blockquote>                     
                    <br>
                  `;

                  toast({
                    type: 'warning',
                    message: message,
                  });

                  return;
                }

                this.#register(dataFields);
              }
            }
          ]
        }
      ]
    };
  }

  /**
   * Cria conteúdo informativo exibido na lateral da página.
   * @private
   */
  async #createAsideText() {
    return {
      element: 'section', parent: 'aside', id: 'aside-form', isGrid: true, content: [
        { element: 'h2', html: dic.register.knowModules, class: 'center' },
        { element: 'p', html: dic.register.explanation1 },
        { element: 'p', html: dic.register.explanation2 },
        { element: 'p', html: dic.register.explanation3, style: { textAlign: 'left', marginLeft: '0.8rem' } },
        { element: 'p', html: dic.register.explanation4 }
      ]
    };
  }

  /**
   * Cria card de exibição de um módulo com preço, recursos e seleção.
   * @private
   * @param {*} module
   * @param {*} userPrice
   * @param {*} parent
   */
  #createModuleCard(module, userPrice, parent = null) {
    const priceValue = parseFloat(module.price) + parseFloat(userPrice);
    const priceFormatted = this.format.number(priceValue, { decimalPlaces: 2 });
    const price = `${this.#currencyPrefix()} ${priceFormatted}`;
    const isMandatory = this.#toBool(module?.is_mandatory ?? module?.mandatory);
    const shouldShowPrice = !(isMandatory && Number(priceValue) === 0);
    const status = String(module?.status || '').trim();
    const titleContent = [
      { element: 'div', class: 'module-card-title', text: module.display },
      ...(this.#isDevelopment() && status
        ? [{ element: 'tag', color: 1, text: status }]
        : [])
    ];

    const check = this.#buildModuleCheckbox(module);

    const dependencies = { element: 'div', class: 'module-dependencies', isGrid: true, content: [] };
    const recommendations = { element: 'div', class: 'module-recommendations', isGrid: true, content: [] };

    const normalizedDependencies = this.#getDependencies(module)
      .map((dep) => ({ dep, depModuleId: this.#getDepModuleId(dep) }))
      .filter((item) => Boolean(item.depModuleId));
    const normalizedRecommendations = this.#getRecommendations(module)
      .map((item) => ({ recModuleId: this.#getRecommendedModuleId(item) }))
      .filter((item) => Boolean(item.recModuleId));

    if (!isMandatory && normalizedDependencies.length > 0) {
      dependencies.content.push({ element: 'div', class: 'module-card-subtitle', text: dic.register.dependencies });
      dependencies.content.push({ element: 'list', list: [] });

      for (const { depModuleId } of normalizedDependencies) {
        dependencies.content[1].list.push({
          element: 'a',
          text: this.#getModuleName(depModuleId, 'catalog'),
          href: `#${depModuleId}`,
          click: (e) => {
            e.preventDefault();
            this.#scrollToModuleCard(depModuleId);
          }
        });
      }
    }

    if (!isMandatory && normalizedRecommendations.length > 0) {
      recommendations.content.push({
        element: 'div',
        class: 'module-card-subtitle',
        text: dic.register.recommendations || 'Recomendações:'
      });
      recommendations.content.push({ element: 'list', list: [] });

      for (const { recModuleId } of normalizedRecommendations) {
        recommendations.content[1].list.push({
          element: 'a',
          text: this.#getModuleName(recModuleId, 'catalog'),
          href: `#${recModuleId}`,
          click: (e) => {
            e.preventDefault();
            this.#scrollToModuleCard(recModuleId);
          }
        });
      }
    }

    const moduleImage = this.#getModuleImage(module);
    const content = [
      { element: 'a', name: module.module || module.domain, style: { top: '-2rem' } },
      ...(moduleImage
        ? [{
          element: 'image',
          origin: 'media',
          src: moduleImage,
          alt: module.display,
          hasFigure: false,
          class: 'module-card-image'
        }]
        : []),
      { element: 'div', class: 'module-card-header', isGrid: true, content: titleContent },
      ...(shouldShowPrice ? [{ element: 'div', class: 'module-price', text: price }] : []),
      ...(this.#isDevelopment() && (module?.domain_id || module?.module_id)
        ? [{ element: 'div', class: 'module-dev-id', text: `${module.domain_id ?? module.module_id}` }]
        : []),
      { element: 'div', class: 'module-description', text: module.description },
      { element: 'div', class: 'module-card-subtitle', text: dic.register.features },
      { element: 'list', list: module.featureNames },
      ...(dependencies.content.length > 0 ? [dependencies] : []),
      ...(recommendations.content.length > 0 ? [recommendations] : []),
      { element: 'div', class: 'module-space' },
      check,
    ];

    if (parent) {
      return {
        element: 'div',
        parent: parent,
        class: `module-card${this.selectedModules.has(module.module || module.domain) ? ' module-card-selected' : ''}`,
        isGrid: true,
        content: content
      };
    } else {
      return {
        element: 'div',
        class: `module-card${this.selectedModules.has(module.module || module.domain) ? ' module-card-selected' : ''}`,
        isGrid: true,
        content: content
      };
    }
  }

  /**
   * Cria container principal da listagem de módulos.
   * @private
   */
  async #createModuleList() {
    return {
      element: 'form', parent: 'main', id: 'main-form', isGrid: true, content: [
        { element: 'section', id: 'module-list', cols: 2 }
      ]
    }
  }

  /**
   * Retorna prefixo monetário conforme idioma atual.
   * @private
   */
  #currencyPrefix() {
    return session.get('language') === 'pt' ? 'R$' : 'US$';
  }
  /* ===== Dados e normalizacao ===== */
  /**
   * Mapeia módulos para servidor/suíte de origem.
   * @private
   * @param {*} data
   */
  #buildModuleServers(data) {
    const allSuites = TYPE_KEYS.flatMap((type) => {
      const key = this.#getTypeCatalogKey(type);
      return Array.isArray(data?.[key]) ? data[key] : [];
    });

    const occurrences = new Map();

    for (const item of allSuites) {
      const moduleKey = this.#getModuleKey(item);
      const suiteKey = item?.key;
      if (!moduleKey) continue;
      if (!occurrences.has(moduleKey)) occurrences.set(moduleKey, new Set());
      if (suiteKey) occurrences.get(moduleKey).add(suiteKey);
    }

    const servers = new Map();
    occurrences.forEach((suiteKeys, moduleKey) => {
      if (suiteKeys.size === 0) return;
      const server = suiteKeys.size > 1 ? 'shared' : Array.from(suiteKeys)[0];
      servers.set(moduleKey, server);
    });

    return servers;
  }

  /**
   * Carrega dados base de suítes e faixas de preço por usuário.
   * @private
   */
  async #getData() {
    if (sharedCatalogCache) {
      return sharedCatalogCache;
    }

    if (!sharedCatalogPromise) {
      sharedCatalogPromise = (async () => {
        const language = session.get('language');
        const catalog = await accountsClient.getSignupCatalog({ language }, { loader: false });
        const suitesPersonal = catalog?.status === 'success' ? (catalog?.data?.suitesPersonal || []) : [];
        const suitesAutonomous = catalog?.status === 'success' ? (catalog?.data?.suitesAutonomous || []) : [];
        const suitesMei = catalog?.status === 'success' ? (catalog?.data?.suitesMei || []) : [];
        const suitesEnterprise = catalog?.status === 'success' ? (catalog?.data?.suitesEnterprise || []) : [];
        const userPrice = catalog?.status === 'success' ? (catalog?.data?.userPrice || []) : [];
        const moduleServersMap = this.#buildModuleServers({ suitesPersonal, suitesAutonomous, suitesMei, suitesEnterprise });
        const moduleServers = Object.fromEntries(moduleServersMap.entries());

        sharedCatalogCache = {
          suitesPersonal: this.#prepareSuitesData(suitesPersonal, moduleServers),
          suitesAutonomous: this.#prepareSuitesData(suitesAutonomous, moduleServers),
          suitesMei: this.#prepareSuitesData(suitesMei, moduleServers),
          suitesEnterprise: this.#prepareSuitesData(suitesEnterprise, moduleServers),
          userPrice,
          moduleServers
        };

        return sharedCatalogCache;
      })().finally(() => {
        sharedCatalogPromise = null;
      });
    }

    return await sharedCatalogPromise;
  }

  async prefetchCatalog() {
    await this.#getData();
  }

  /**
   * Obtém dependências declaradas para um módulo.
   * @private
   * @param {*} module
   */
  #getDependencies(module) {
    const dependencies = this.#coerceArray(module?.dependencies);
    return dependencies
      .map((dep) => {
        if (typeof dep === 'string') {
          const value = dep.trim();
          if (!value) return null;
          return { module: value };
        }

        if (!dep || typeof dep !== 'object') return null;
        const moduleId = String(
          dep.module
          || dep.key
          || dep.name
          || dep.depends_on
          || dep.dependsOn
          || dep.depends_on_module
          || dep.depends_on_module_name
          || ''
        ).trim();
        if (!moduleId) return null;
        return { ...dep, module: moduleId, key: dep.key || moduleId };
      })
      .filter(Boolean);
  }

  #getRecommendations(module) {
    const rawRecommendations = module?.recommendations
      ?? module?.recommended
      ?? module?.recommended_modules
      ?? module?.modules_recommended
      ?? module?.recommendedModules;

    const recommendations = this.#coerceArray(rawRecommendations);
    return recommendations
      .map((item) => {
        if (typeof item === 'string') {
          const value = item.trim();
          if (!value) return null;
          return { module: value };
        }

        if (!item || typeof item !== 'object') return null;
        const moduleId = String(
          item.module
          || item.key
          || item.name
          || item.recommended_module
          || item.recommended_module_name
          || item.recommendedModule
          || ''
        ).trim();

        if (moduleId) return { ...item, module: moduleId, key: item.key || moduleId };

        const byId = String(
          item.id
          || item.domain_id
          || item.module_id
          || item.recommended_domain_id
          || item.recommended_module_id
          || item.recommendedModuleId
          || ''
        ).trim();
        if (!byId) return null;
        return { ...item, id: byId };
      })
      .filter(Boolean);
  }

  /**
   * Filtra dependências ainda não selecionadas.
   * @private
   * @param {*} dependencies
   */
  #getDependenciesToAdd(dependencies) {
    return dependencies.filter(dep => {
      const depModuleId = this.#getDepModuleId(dep);
      return depModuleId && !this.selectedModules.has(depModuleId);
    });
  }

  /**
   * Obtém identificador de módulo de uma dependência.
   * @private
   * @param {*} dep
   */
  #getDepModuleId(dep) {
    if (typeof dep === 'string') return dep.trim();
    const directModule = String(
      dep?.module
      || dep?.domain
      || dep?.key
      || dep?.name
      || dep?.depends_on
      || dep?.dependsOn
      || dep?.depends_on_module
      || dep?.depends_on_module_name
      || ''
    ).trim();
    if (directModule) return directModule;

    const depId = String(dep?.id || dep?.domain_id || dep?.module_id || dep?.depends_on_domain_id || dep?.depends_on_module_id || '').trim();
    if (!depId) return '';

    return this.moduleNameById.get(depId)
      || Array.from(this.moduleMeta.values()).find((item) => String(item?.id || '') === depId)?.module
      || '';
  }

  #getRecommendedModuleId(item) {
    if (typeof item === 'string') return item.trim();

    const directModule = String(
      item?.module
      || item?.domain
      || item?.key
      || item?.name
      || item?.recommended_module
      || item?.recommended_module_name
      || item?.recommendedModule
      || ''
    ).trim();
    if (directModule) return directModule;

    const recId = String(
      item?.id
      || item?.domain_id
      || item?.module_id
      || item?.recommended_domain_id
      || item?.recommended_module_id
      || item?.recommendedModuleId
      || ''
    ).trim();
    if (!recId) return '';

    return this.moduleNameById.get(recId)
      || Array.from(this.moduleMeta.values()).find((meta) => String(meta?.id || '') === recId)?.module
      || '';
  }

  /**
   * Obtém inputs de seleção associados a um módulo.
   * @private
   * @param {*} moduleId
   */
  #getModuleInputs(moduleId) {
    if (!moduleId) return [];
    const moduleInputs = Array.from(document.querySelectorAll('input.module-selector'));
    const matched = moduleInputs.filter(input => this.#getModuleKeyFromInput(input) === moduleId);
    if (matched.length > 0) return matched;
    // Fallback: inputs que usem data-module-id
    const dataMatched = Array.from(document.querySelectorAll(`input[data-module-id="${moduleId}"]`));
    if (dataMatched.length > 0) return dataMatched;
    // Fallback adicional: match por id prefixado (ex.: module-select-<module>-<suite>) para módulos em mais de uma suíte
    return Array.from(document.querySelectorAll(`input[id^="module-select-${moduleId}-"]`));
  }

  /**
   * Obtém chave de módulo a partir de um input de seleção.
   * @private
   * @param {*} input
   */
  #getModuleKeyFromInput(input) {
    if (!input) return null;
    return input.dataset?.key
      || input.dataset?.moduleId
      || input.getAttribute('data-module-id')
      || input.value;
  }

  /**
   * Obtém lista de módulos vinculados a uma suíte.
   * @private
   * @param {*} groupName
   */
  #getSuiteModules(groupName) {
    return this.suiteModules.get(groupName) || [];
  }

  /**
   * Obtém faixa de preço por quantidade de usuários.
   * @private
   * @param {*} usersQtyValue
   */
  #getUserPriceRange(usersQtyValue) {
    const userPriceRanges = Array.isArray(this.data?.userPrice) ? this.data.userPrice : [];
    return userPriceRanges.find(r => {
      const min = Number(r?.min_users ?? 0);
      const max = r?.max_users == null ? Infinity : Number(r.max_users);
      return usersQtyValue >= min && usersQtyValue <= max;
    });
  }

  /**
   * Obtém quantidade atual de usuários do formulário.
   * @private
   */
  #getUsersQtyValue() {
    const config = this.#getTypeUsersConfig();
    const usersQtyEl = document.getElementById('users-qty');
    if (usersQtyEl) {
      const rawValue = Number(usersQtyEl.value ?? usersQtyEl.getAttribute('value') ?? config.value);
      const normalized = this.#normalizeUsersQtyForType(rawValue, this.currentType);
      if (String(usersQtyEl.value) !== String(normalized)) {
        usersQtyEl.value = normalized;
      }
      return normalized;
    }
    const stored = Number(session.get('usersQty'));
    return this.#normalizeUsersQtyForType(stored, this.currentType);
  }

  /**
   * Sincroniza estado visual de inputs de um módulo.
   * @private
   * @param {*} moduleId
   * @param {*} checkedOverride
   * @param {*} origin
   */
  #syncModuleState(moduleId, checkedOverride = null, origin = null) {
    if (!moduleId) return;
    const checked = checkedOverride ?? this.selectedModules.has(moduleId);
    const disabled = this.dependencyLocks.has(moduleId);
    this.#getModuleInputs(moduleId).forEach(input => {
      if (!origin || input !== origin) {
        input.checked = checked;
      }
      input.disabled = disabled;
      const card = input.closest('.module-card');
      if (card) {
        card.classList.toggle('module-card-selected', checked);
      }
      const switchText = input.closest('.module-select')?.querySelector('.switch-text');
      if (switchText) {
        switchText.textContent = checked
          ? (dic.register.selectedModule || dic.register.selectModule)
          : dic.register.selectModule;
      }
    });
  }

  /**
   * Atualiza payload de módulos selecionados enviado no cadastro.
   * @private
   * @param {*} moduleId
   * @param {*} checked
   * @param {*} moduleData
   */
  #updateSelectedCards(moduleId, checked, moduleData = null) {
    if (!moduleId) return;
    const meta = moduleData || this.moduleMeta.get(moduleId) || {};
    const id = meta.domain_id ?? meta.module_id ?? meta.id ?? moduleId;
    const key = meta.module ?? meta.domain ?? meta.key ?? moduleId;
    const server = meta.server ?? meta.key ?? null;

    if (checked) {
      const idx = this.selectedCards.findIndex(item => item.key === key);
      const payload = { id, key, server };
      if (idx >= 0) {
        this.selectedCards[idx] = payload;
      } else {
        this.selectedCards.push(payload);
      }
    } else {
      this.selectedCards = this.selectedCards.filter(item => item.key !== key);
    }
  }

  /**
   * Atualiza toggle de suíte com base na seleção dos módulos.
   * @private
   * @param {*} groupName
   */
  #updateSuiteToggle(groupName) {
    if (!groupName) return;
    const modules = this.#getSuiteModules(groupName);
    if (modules.length === 0) return;
    const allSelected = modules.every(id => this.selectedModules.has(id));
    const suiteToggle = document.getElementById(`suite-select-${groupName}`);
    if (suiteToggle) {
      this.syncingSelection = true;
      suiteToggle.checked = allSelected;
      this.syncingSelection = false;
    }
  }

  /**
   * Atualiza toggle da suíte associada ao módulo informado.
   * @private
   * @param {*} moduleId
   */
  #updateSuiteToggleForModule(moduleId) {
    const meta = this.moduleMeta.get(moduleId);
    if (!meta?.groupName) return;
    this.#updateSuiteToggle(meta.groupName);
  }
  /* ===== Interacao e fluxo ===== */
  /**
   * Trata desmarcação de módulo e limpeza de dependências relacionadas.
   * @private
   * @param {*} module
   * @param {*} sourceInput
   */
  #handleModuleUncheck(module, sourceInput) {
    this.dependencyPromptOpen.delete(this.#getModuleKey(module));
    this.#closeDependencyPrompt(this.#getModuleKey(module));
    this.syncingSelection = true;
    this.#toggleModuleSelection(this.#getModuleKey(module), false, sourceInput, true, module);
    this.#updateDependencyLocks(module, false);
    this.syncingSelection = false;
    this.#updateCart();
    this.#clearModuleInteraction(this.#getModuleKey(module));
  }

  /**
   * Trata troca de tipo de cadastro e re-renderiza listagem.
   * @private
   * @param {*} value
   */
  async #handleTypeChange(value) {
    this.currentType = value;
    this.#setUsersQtyState(value);
    this.#resetModuleListScroll();
    await this.#rerenderModules();
    this.#resetModuleListScroll();
    this.#updateCart();
  }

  /**
   * Carrega e renderiza grupos de módulos no layout atual.
   * @private
   * @param {*} view
   * @param {*} data
   * @param {*} parent
   */
  async #loadGroups(view, data, parent = false) {
    const renderVersion = ++this.renderVersion;
    this.modulePrices.clear();
    this.moduleServers = this.#buildModuleServers(data);
    this.moduleMeta.clear();
    this.moduleNameById.clear();
    this.suiteModules.clear();

    const cards = [];
    let lastGroup = null;

    function diffArrays(a, b) {
      const setA = new Set(a);
      const setB = new Set(b);

      return {
        apenasEmArquivo: [...setA].filter(x => !setB.has(x)),
        apenasEmBanco: [...setB].filter(x => !setA.has(x))
      };
    }

    const viewDataKey = this.#getTypeCatalogKey(this.currentType);
    const viewDataSource = Array.isArray(data?.[viewDataKey]) ? data[viewDataKey] : [];
    const viewData = viewDataSource.map((item) => ({ ...item }));

    for (const i in viewData) {
      this.moduleNameById.set(String(viewData[i].domain_id ?? viewData[i].module_id), viewData[i].module || viewData[i].domain);
      viewData[i].groupName = this.#getSuiteName(viewData[i].key, 'catalog');
      viewData[i].display = this.#getModuleName(viewData[i].module || viewData[i].domain, 'catalog');
      viewData[i].description = dic.modules?.[viewData[i].module || viewData[i].domain]?.description || '';
      viewData[i].server = this.moduleServers.get(viewData[i].module || viewData[i].domain) || viewData[i].key;
      this.moduleMeta.set(viewData[i].module || viewData[i].domain, {
        id: viewData[i].domain_id ?? viewData[i].module_id,
        module: viewData[i].module || viewData[i].domain,
        key: viewData[i].module || viewData[i].domain,
        is_mandatory: this.#toBool(viewData[i].is_mandatory ?? viewData[i].mandatory),
        server: viewData[i].server,
        groupName: viewData[i].groupName,
        dependencies: this.#getDependencies(viewData[i])
      });
      viewData[i].featureNames = this.#coerceArray(
        dic.modules?.[viewData[i].module || viewData[i].domain]?.features
      )
        .filter((feature) => typeof feature === 'string' && feature.trim())
        .map((feature) => feature.trim());
    }

    const sortedData = this.#sort(viewData).filter((item) => {
      if (this.#isDevelopment()) return true;
      return (item?.is_active === undefined ? true : this.#toBool(item?.is_active))
        && (item?.suite_is_active === undefined ? true : this.#toBool(item?.suite_is_active));
    });
    const suiteHasOptionalModules = new Map();

    for (const item of sortedData) {
      if (!item?.groupName) continue;
      const hasOptional = suiteHasOptionalModules.get(item.groupName) || false;
      suiteHasOptionalModules.set(item.groupName, hasOptional || !this.#toBool(item?.is_mandatory ?? item?.mandatory));
    }

    // Se estiver re-renderizando dentro de um parent, limpa antes de recriar os cards
    if (parent && parent instanceof Element) {
      // preferir limpeza direta do DOM para evitar headers duplicados
      while (parent.firstChild) parent.removeChild(parent.firstChild);
    } else if (parent && typeof parent.empty === 'function') {
      parent.empty();
    }

    for (const module of sortedData) {
      // se uma nova renderização foi iniciada, cancela esta
      if (renderVersion !== this.renderVersion) return;
      if (module.key !== lastGroup) {
        lastGroup = module.key;
        if (!this.suiteNames.includes(module.groupName)) {
          this.suiteNames.push(module.groupName);
        }
        this.suiteModules.set(module.groupName, []);
        const canSelectWholeSuite = module.key !== 'core' && suiteHasOptionalModules.get(module.groupName);
        const suiteButton = canSelectWholeSuite ? [
          {
            element: 'checkbox',
            class: 'suite-selector',
            fbClass: 'suite-select',
            id: `suite-select-${module.groupName}`,
            text: dic.register.selectSuite,
            appearance: 'switch',
            checked: this.selectedModules.has(module.module || module.domain),
            click: (e) => this.#onSuiteToggle(module.groupName, e.currentTarget.checked, e.currentTarget),
          }
        ] : [];

        if (parent) {
          cards.push({
            element: 'h3', parent: parent, class: 'module-group', text: module.groupName, isGrid: true, content: suiteButton
          });
        } else {
          cards.push({ element: 'h3', class: 'module-group', text: module.groupName, isGrid: true, content: suiteButton });
        }

      }
      this.modulePrices.set(module.module || module.domain, {
        total: Number(module.price) || 0,
        base: Number(module.price) || 0,
        users: 0
      });

      const card = this.#createModuleCard(module, 0, parent);
      cards.push(card);
      // adiciona módulo na lista da suíte correspondente
      const suiteList = this.#getSuiteModules(module.groupName);
      suiteList.push(module.module || module.domain);
      this.suiteModules.set(module.groupName, suiteList);
    }

    if (parent) {
      if (renderVersion !== this.renderVersion) return;
      const renderSlice = async (start, size) => {
        const slice = cards.slice(start, start + size);
        if (slice.length === 0) return start;
        await create(slice);
        this.#applySelections();
        this.#updateCart();
        return start + slice.length;
      };

      let cursor = await renderSlice(0, this.initialRenderBatchSize);
      parent.dataset.initialized = 'true';

      const continueRendering = async () => {
        while (cursor < cards.length) {
          await this.#yieldToBrowser();
          if (renderVersion !== this.renderVersion) return;
          cursor = await renderSlice(cursor, this.renderBatchSize);
        }
      };

      void continueRendering();
    } else {
      return cards;
    }
  }

  /**
   * Aplica lock curto para evitar cliques duplicados em módulo.
   * @private
   * @param {*} moduleId
   * @param {*} ms
   */
  #lockModuleInteraction(moduleId, ms = 400) {
    if (!moduleId) return false;
    if (this.clickLocks.has(moduleId)) return false;
    const timer = setTimeout(() => this.clickLocks.delete(moduleId), ms);
    this.clickLocks.set(moduleId, timer);
    return true;
  }

  /**
   * Trata interação de seleção/deseleção de módulo.
   * @private
   * @param {*} module
   * @param {*} sourceInput
   */
  #onModuleToggle(module, sourceInput) {
    const isChecked = sourceInput.checked;
    if (this.syncingSelection) return;

    if (!this.#lockModuleInteraction(this.#getModuleKey(module))) {
      sourceInput.checked = !isChecked;
      return;
    }

    const dependencies = this.#getDependencies(module);
    const depsToAdd = this.#getDependenciesToAdd(dependencies);

    if (!isChecked) {
      this.#handleModuleUncheck(module, sourceInput);
      return;
    }

    if (dependencies.length === 0 || depsToAdd.length === 0) {
      this.#selectModuleWithoutPrompt(module, sourceInput);
      return;
    }

    if (this.dependencyPromptOpen.has(this.#getModuleKey(module))) {
      this.#clearModuleInteraction(this.#getModuleKey(module));
      return;
    }

    this.#showDependencyPrompt(module, depsToAdd, dependencies, sourceInput);
  }
  /* ===== Registro ===== */
  /**
   * Trata seleção em lote de módulos de uma suíte.
   * @private
   * @param {*} groupName
   * @param {*} checked
   * @param {*} origin
   */
  #onSuiteToggle(groupName, checked, origin) {
    if (!groupName) return;
    const modules = this.#getSuiteModules(groupName);
    if (modules.length === 0) return;
    this.syncingSelection = true;
    for (const moduleId of modules) {
      const meta = this.moduleMeta.get(moduleId) || { module: moduleId, groupName };
      if (!checked && this.#toBool(meta?.is_mandatory ?? meta?.mandatory)) {
        this.#toggleModuleSelection(moduleId, true, origin, false, meta);
        continue;
      }
      if (checked) {
        this.#toggleModuleSelection(moduleId, true, origin, true, meta);
        const deps = this.#getDependencies(meta);
        for (const dep of deps) {
          const depId = this.#getDepModuleId(dep);
          if (!depId) continue;
          const depMeta = this.moduleMeta.get(depId) || { module: depId };
          this.#toggleModuleSelection(depId, true, origin, false, depMeta);
        }
        this.#updateDependencyLocks(meta, true);
      } else {
        this.#toggleModuleSelection(moduleId, false, origin, true, meta);
        this.#updateDependencyLocks(meta, false);
      }
    }
    this.syncingSelection = false;
    this.#updateSuiteToggle(groupName);
    this.#updateCart();
  }

  /**
   * MsgBox de ciência do trial (textos do dicionário; não revela sinais nem mecanismos de detecção).
   * @private
   * @param {string} decision 'consumed' | 'restricted'
   * @returns {Promise<boolean>} true somente em "Sim".
   */
  #confirmTrialDecision(decision) {
    const copy = decision === 'consumed' ? dic.trial?.consumed : dic.trial?.restrictedEvaluation;
    if (!copy) return Promise.resolve(false);
    return new Promise((resolve) => {
      let settled = false;
      const finish = (value) => { if (!settled) { settled = true; resolve(value); } };
      msgbox({
        type: 'yn',
        title: copy.title,
        message: copy.message,
        click: () => finish(true),
        nclick: () => finish(false),
        onClose: () => finish(false),
      });
    });
  }

  /**
   * Executa persistência do cadastro e fluxo pós-registro.
   * @private
   * @param {*} dataFields
   */
  async #register(dataFields) {
    if (this.registering) return;
    this.registering = true;

    if (!dataFields['users-qty'] || dataFields['users-qty'] == '0') {
      dataFields['users-qty'] = this.#getTypeUsersConfig().value;
    }

    try {
      dataFields.device_id = getDeviceId();
      // O mesmo formulário reaproveita a chave mesmo após timeout ou reenvio.
      // Alterar a identidade do cadastro inicia outra tentativa; o token de ciência permanece vinculado à anterior.
      const attemptIdentity = JSON.stringify([
        String(dataFields.email || '').trim().toLowerCase(),
        String(dataFields.full_name || dataFields.user_name || '').trim(),
        String(dataFields.type || dataFields['type-register'] || '').trim(),
      ]);
      if (this.signupAttempt?.identity !== attemptIdentity) {
        this.signupAttempt = {
          identity: attemptIdentity,
          key: globalThis.crypto?.randomUUID?.()
            || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`,
        };
      }
      dataFields.idempotency_key = this.signupAttempt.key;
      let result = await accountsClient.saveSignup(dataFields);
      // Trial já utilizado ou avaliação restrita: nada foi criado ainda. "Não" encerra; "Sim" reenvia com o token de ciência.
      if (result?.status === 'trial_confirmation_required') {
        if (!await this.#confirmTrialDecision(result.decision)) return;
        result = await accountsClient.saveSignup({ ...dataFields, trial_ack: result.ack_token });
      }
      if (result?.status === 'completed' && result?.provisioning_id && result?.tenant_id && result?.user_id) {
        this.signupAttempt = null;
        const message = `
          <div style="font-weight: bold;">${dic.register.success1}</div>
          <div>${dic.register.successNoEmail || dic.register.success3}</div>
        `;
        toast({ type: 'success', message: message });

        document.querySelector('.module-aside-cart')?.remove();
        document.querySelectorAll('.menubar .active').forEach((node) => node.classList.remove('active'));
        document.querySelectorAll('header button').forEach((el) => el.enable());

        document.querySelector('aside').empty();
        document.querySelector('main')?.empty?.();
        document.querySelector('aside').classList.remove('module-aside');
        document.querySelector('main').classList.remove('module-main');

        const [loginLock, routerModule] = await Promise.all([
          import('./loginLock').then((module) => module.default),
          import('../../js/router'),
        ]);
        await create({
          element: 'login',
          code: 'admindigital',
          branding: dic?.branding || {},
          loginLock,
          onRedirect: async ({ authenticated }) => {
            if (authenticated) {
              window.history.replaceState({}, '', '/logged');
            }
            await routerModule?.RefreshCurrentRoute?.();
          },
        });
      } else {
        const code = String(result?.code || result?.error || 'SIGNUP_INVALID_REQUEST');
        toast({ type: 'error', message: dic.register.errors?.[code] || code });
      }
    } catch (error) {
      const payload = error?.payload && typeof error.payload === 'object' ? error.payload : {};
      const code = String(payload.code || payload.error || error?.message || 'SIGNUP_INVALID_REQUEST');
      toast({ type: 'error', message: dic.register.errors?.[code] || code });
    } finally {
      this.registering = false;
    }
  }

  /**
   * Re-renderiza listagem de módulos mantendo estado atual.
   * @private
   */
  async #rerenderModules() {
    const groupView = document.querySelector('input[name="module-view"]:checked')?.value || 'suites';
    const moduleList = document.getElementById('module-list');
    if (moduleList) {
      await this.#loadGroups(groupView, this.data, moduleList);
    }
  };

  /**
   * Reposiciona a lista de módulos para o topo.
   * @private
   */
  #resetModuleListScroll() {
    const moduleList = document.getElementById('module-list');
    if (!moduleList) return;
    moduleList.scrollTop = 0;
  }

  /**
   * Seleciona módulo diretamente quando não há prompt de dependências.
   * @private
   * @param {*} module
   * @param {*} sourceInput
   */
  #selectModuleWithoutPrompt(module, sourceInput) {
    this.#updateDependencyLocks(module, true);
    this.#toggleModuleSelection(this.#getModuleKey(module), true, sourceInput, true, module);
    this.#updateCart();
    this.#clearModuleInteraction(this.#getModuleKey(module));
  }

  /**
   * Define estado habilitado/desabilitado dos inputs de um módulo.
   * @private
   * @param {*} moduleId
   * @param {*} disabled
   */
  #setModuleDisabled(moduleId, disabled) {
    this.#getModuleInputs(moduleId).forEach(input => {
      input.disabled = disabled;
    });
  }

  /**
   * Configura estado do campo de quantidade de usuários por tipo.
   * @private
   * @param {*} type
   */
  #setUsersQtyState(type) {
    const usersQty = document.getElementById('users-qty');
    if (!usersQty) return;

    const fb = usersQty.closest('.fieldbox');
    const toggleDisabled = (disabled) => {
      usersQty.disabled = disabled;
      if (fb) {
        fb.classList.toggle('disabled', disabled);
        if (typeof fb.show === 'function') fb.show();
        const buttons = fb.querySelectorAll('button');
        buttons.forEach(button => {
          button.disabled = disabled;
        });
      }
    };

    const currentValue = Number(usersQty.value ?? 0);
    if (Number.isFinite(currentValue) && currentValue >= 5) {
      session.set('usersQty', usersQty.value);
    }

    const config = this.#getTypeUsersConfig(type);
    usersQty.min = config.min;
    const preferredValue = config.min >= 5
      ? ((() => {
        const storedUsersQty = Number(session.get('usersQty'));
        return Number.isFinite(storedUsersQty) && storedUsersQty >= config.min
          ? storedUsersQty
          : config.value;
      })())
      : config.value;
    usersQty.value = this.#normalizeUsersQtyForType(preferredValue, type);
    toggleDisabled(config.disabled);

    // força atualização do carrinho/hidden fields
    this.#updateCart();
  }

  /**
   * Exibe confirmação para incluir dependências ao selecionar módulo.
   * @private
   * @param {*} module
   * @param {*} depsToAdd
   * @param {*} dependencies
   * @param {*} sourceInput
   */
  #showDependencyPrompt(module, depsToAdd, dependencies, sourceInput) {
    const message = this.#buildDependencyPromptMessage(depsToAdd);
    let resolved = false;

    const deselectModule = () => {
      if (resolved) return;
      this.dependencyPromptOpen.delete(this.#getModuleKey(module));
      this.syncingSelection = true;
      if (sourceInput) sourceInput.checked = false;
      this.#toggleModuleSelection(this.#getModuleKey(module), false, sourceInput, true);
      this.syncingSelection = false;
      this.#updateCart();
      this.#clearModuleInteraction(this.#getModuleKey(module));
    };

    const selectWithDependencies = () => {
      resolved = true;
      this.dependencyPromptOpen.delete(this.#getModuleKey(module));
      this.#applySelectionWithDependencies(module, dependencies, sourceInput);
      this.#clearModuleInteraction(this.#getModuleKey(module));
    };

    this.dependencyPromptOpen.add(this.#getModuleKey(module));
    msgbox({
      type: 'yn',
      title: dic.register.askDependencies1,
      message,
      class: `module-box module-box-${this.#getModuleKey(module)}`,
      click: selectWithDependencies,
      nclick: deselectModule,
      onClose: () => {
        if (!resolved) {
          deselectModule();
        } else {
          this.#clearModuleInteraction(this.#getModuleKey(module));
        }
      }
    });
  }

  /**
   * Ordena módulos por ordem configurada, grupo e nome de exibição.
   * @private
   * @param {*} rawData
   */
  #sort(rawData) {
    const language = session.get('language') || 'pt';
    const orderKey = 'order';

    // Garante que SEMPRE seja array
    const data = Array.isArray(rawData) ? rawData : [];

    const withSortedFeatures = data.map(item => ({
      ...item,
      featureNames: Array.isArray(item.featureNames)
        ? [...item.featureNames].sort((a, b) =>
          a.localeCompare(b, language, { sensitivity: "accent" })
        )
        : []
    }));

    const sorted = withSortedFeatures.sort((a, b) => {
      const aOrder = Number(a?.[orderKey] ?? Number.POSITIVE_INFINITY);
      const bOrder = Number(b?.[orderKey] ?? Number.POSITIVE_INFINITY);
      if (Number.isFinite(aOrder) || Number.isFinite(bOrder)) {
        const byOrder = aOrder - bOrder;
        if (byOrder !== 0) return byOrder;
      }

      const aGroup = a.groupName;
      const bGroup = b.groupName;
      if (aGroup != null && bGroup != null) {
        const byGroup = aGroup.localeCompare(bGroup, language, { sensitivity: "accent" });
        if (byGroup !== 0) return byGroup;
      }

      const aDisplay = a.display ?? "";
      const bDisplay = b.display ?? "";
      return aDisplay.localeCompare(bDisplay, language, { sensitivity: "accent" });
    });

    return sorted;
  }

  /**
   * Sincroniza inputs de módulo delegando para o estado central.
   * @private
   * @param {*} moduleId
   * @param {*} checked
   * @param {*} origin
   */
  #syncModuleInputs(moduleId, checked, origin) {
    this.#syncModuleState(moduleId, checked, origin);
  }

  /**
   * Atualiza seleção interna de um módulo e efeitos relacionados.
   * @private
   * @param {*} moduleId
   * @param {*} checked
   * @param {*} origin
   * @param {*} isManual
   * @param {*} moduleData
   */
  #toggleModuleSelection(moduleId, checked, origin = null, isManual = false, moduleData = null) {
    if (!moduleId) return;
    const meta = moduleData || this.moduleMeta.get(moduleId) || {};
    const isMandatory = this.#toBool(meta?.is_mandatory ?? meta?.mandatory);

    if (checked) {
      this.selectedModules.add(moduleId);
      if (isManual) this.manuallySelectedModules.add(moduleId);
    } else {
      if (isMandatory) {
        this.#updateSelectedCards(moduleId, true, meta);
        this.#syncModuleState(moduleId, true, origin);
        this.#updateSuiteToggleForModule(moduleId);
        return;
      }
      this.selectedModules.delete(moduleId);
      if (isManual || !this.dependencyLocks.has(moduleId)) {
        this.manuallySelectedModules.delete(moduleId);
      }
    }
    this.#updateSelectedCards(moduleId, checked, moduleData);
    this.#syncModuleState(moduleId, checked, origin);
    this.#updateSuiteToggleForModule(moduleId);
  }

  /**
   * Atualiza totais e campos ocultos do carrinho.
   * @private
   */
  #updateCart() {
    const moduleCountEl = document.getElementById('module-count');
    const userCountEl = document.getElementById('user-count');
    const modulesQtyTextEl = document.getElementById('text-modules-qty');
    const modulesValueTextEl = document.getElementById('text-modules-value');
    const usersQtyTextEl = document.getElementById('text-users-qty');
    const usersValueTextEl = document.getElementById('text-users-value');
    const monthlyTextEl = document.getElementById('text-monthly-value');
    const modulesQtyHidden = document.getElementById('cart_modules_qty');
    const modulesValueHidden = document.getElementById('cart_modules_value');
    const usersQtyHidden = document.getElementById('cart_users_qty');
    const usersValueHidden = document.getElementById('cart_users_value');
    const monthlyValueHidden = document.getElementById('cart_monthly_value');

    const usersQtyValue = this.#getUsersQtyValue();
    const cart = this.#calculateCart(usersQtyValue);

    if (moduleCountEl) moduleCountEl.textContent = cart.moduleCount;
    if (userCountEl) userCountEl.textContent = usersQtyValue;
    if (modulesQtyTextEl) modulesQtyTextEl.textContent = cart.moduleCount;
    if (modulesValueTextEl) modulesValueTextEl.textContent = cart.formattedModules;
    if (usersQtyTextEl) usersQtyTextEl.textContent = usersQtyValue;
    if (usersValueTextEl) usersValueTextEl.textContent = cart.formattedUsers;
    if (monthlyTextEl) monthlyTextEl.textContent = cart.formattedTotal;
    if (modulesQtyHidden) modulesQtyHidden.value = cart.moduleCount;
    if (modulesValueHidden) modulesValueHidden.value = cart.modulesTotal;
    if (usersQtyHidden) usersQtyHidden.value = usersQtyValue;
    if (usersValueHidden) usersValueHidden.value = cart.usersTotal;
    if (monthlyValueHidden) monthlyValueHidden.value = cart.total;
  }

  /**
   * Atualiza locks de dependência ao selecionar ou remover módulos.
   * @private
   * @param {*} module
   * @param {*} adding
   */
  #updateDependencyLocks(module, adding) {
    const dependencies = this.#getDependencies(module);
    if (dependencies.length === 0) return;

    for (const dep of dependencies) {
      const depModuleId = this.#getDepModuleId(dep);
      if (!depModuleId) continue;

      const current = this.dependencyLocks.get(depModuleId) || 0;
      const next = Math.max(0, current + (adding ? 1 : -1));

      if (next > 0) {
        this.dependencyLocks.set(depModuleId, next);
      } else {
        this.dependencyLocks.delete(depModuleId);
      }

      this.#setModuleDisabled(depModuleId, next > 0);

      if (!adding && next === 0 && !this.manuallySelectedModules.has(depModuleId)) {
        this.syncingSelection = true;
        this.selectedModules.delete(depModuleId);
        this.#updateSelectedCards(depModuleId, false);
        this.#syncModuleInputs(depModuleId, false);
        this.syncingSelection = false;
      }
    }
  }
  /* ===== API publica ===== */

  /**
   * Renderiza a experiência de escolha de módulos e inicializa estado visual.
   * @public
   */
  async modules() {
    this.suiteNames = [];
    document.body.classList.add('modules-mounting');

    try {
      this.data = await this.#getData();
      this.moduleServers = this.#buildModuleServers(this.data);

      await create([
        await this.#createAsideText(),
        await this.#createModuleList(),
        await this.#createAsideCart(),
      ]);

      const moduleList = document.getElementById('module-list');
      if (moduleList) {
        await this.#loadGroups('suites', this.data, moduleList);
      }

      this.#updateCart();
      this.#applySelections();
      document.querySelector('aside').classList.add('module-aside');
      document.querySelector('main').classList.add('module-main');

      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
    } finally {
      document.body.classList.remove('modules-mounting');
    }
  }
}

const mod = new Modules();
export default () => mod.modules();
export const prefetchModulesCatalog = () => mod.prefetchCatalog();
